import { useEffect, useRef, useState } from 'react';
import { call } from '../api';
import { navigate } from '../router';

/**
 * Telefoon als afstandsbediening — de kant van het scherm (tv/laptop).
 * Openen via `openRemotePairing()` (bijv. vanuit Instellingen). Toont een code; toetsen van de
 * telefoon komen binnen als gewone toetsaanslagen, zodat de hele app ermee te bedienen is.
 */

const OPEN_EVENT = 'tumedia:remote-pair';
const CODE_KEY = 'tumedia.remoteScreen';

export function openRemotePairing(): void {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

function pressKey(key: string): void {
  const target = (document.activeElement as HTMLElement | null) ?? document.body;
  const ev = new KeyboardEvent('keydown', { key, bubbles: true, cancelable: true });
  const handled = !target.dispatchEvent(ev);
  // Enter op een knop of link: klikken (een nagemaakte toets doet dat niet vanzelf)
  if (key === 'Enter' && !handled && target !== document.body) target.click();
}

export function RemoteScreen() {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(CODE_KEY);
    } catch {
      return null;
    }
  });
  const [paired, setPaired] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const flash = useRef<number | undefined>(undefined);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  // Code aanvragen wanneer het venster opent en er nog geen is
  useEffect(() => {
    if (!open || code) return;
    call<{ code: string }>('/api/remote/screen', { method: 'POST' })
      .then((r) => {
        setCode(r.code);
        try {
          sessionStorage.setItem(CODE_KEY, r.code);
        } catch {
          // geen opslag
        }
      })
      .catch(() => setError('Kon geen code aanvragen.'));
  }, [open, code]);

  // Luisteren naar de telefoon
  useEffect(() => {
    if (!code) return;
    const es = new EventSource(`/api/remote/screen/${code}/events`);
    let failures = 0;
    es.onopen = () => {
      failures = 0;
    };
    es.onerror = () => {
      failures += 1;
      // Code onbekend (bijv. server herstart): opnieuw beginnen
      if (failures > 3) {
        es.close();
        try {
          sessionStorage.removeItem(CODE_KEY);
        } catch {
          // geen opslag
        }
        setCode(null);
        setPaired(null);
      }
    };
    es.addEventListener('paired', (e) => {
      const d = JSON.parse((e as MessageEvent).data) as { by: string };
      setPaired(d.by);
      window.clearTimeout(flash.current);
      flash.current = window.setTimeout(() => setOpen(false), 1800);
    });
    es.addEventListener('key', (e) => {
      const { key } = JSON.parse((e as MessageEvent).data) as { key: string };
      if (key === 'home') navigate('/');
      else if (key === 'search') navigate('/search');
      else if (key === 'info') pressKey('i');
      else if (key === 'seek-back' || key === 'seek-forward') window.dispatchEvent(new CustomEvent('tumedia:seek', { detail: key === 'seek-back' ? -10 : 30 }));
      else pressKey(key);
    });
    return () => es.close();
  }, [code]);

  const stop = () => {
    try {
      sessionStorage.removeItem(CODE_KEY);
    } catch {
      // geen opslag
    }
    setCode(null);
    setPaired(null);
    setOpen(false);
  };

  if (!open) return null;
  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="Telefoon als afstandsbediening" onClick={() => setOpen(false)}>
      <div className="modal remote-pair" onClick={(e) => e.stopPropagation()}>
        <h2>Telefoon als afstandsbediening</h2>
        {error && <p className="error">{error}</p>}
        {paired ? (
          <p>✓ Gekoppeld met {paired}</p>
        ) : (
          <>
            <p className="muted">Open op je telefoon (ingelogd in tumedia):</p>
            <p><strong>{location.origin}/remote</strong></p>
            <p className="muted">en vul deze code in:</p>
            <div className="together-big remote-code">{code ?? '······'}</div>
          </>
        )}
        <div className="row" style={{ gap: 10, justifyContent: 'center' }}>
          <button className="btn btn-primary" autoFocus onClick={() => setOpen(false)}>Sluiten</button>
          {code && <button className="btn" onClick={stop}>Ontkoppelen</button>}
        </div>
      </div>
    </div>
  );
}
